import { useEffect, useState } from 'react';
import { Loader2, RefreshCw, LogOut, AlertCircle, QrCode } from 'lucide-react';
import { Modal } from '../../../components/Modal';
import { useSocket } from '../../../lib/socket';
import { useToast } from '../../../components/Toast';
import { atendimentoApi } from '../lib/api';
import type { BaileysStatus } from '../lib/tipos';

interface Props {
  /** Ignorado quando `embutido`. */
  aberto?: boolean;
  onFechar?: () => void;
  /**
   * Nome da INSTANCIA (o celular), nao a caixa. Desde a DA-510 a loja pode ter
   * mais de um aparelho na mesma caixa, entao "atendimento" nao identifica um.
   * Sem default: escolher errado apaga a sessao errada.
   */
  chip: string;
  /** Nome que o Ruan deu ao aparelho. So pra exibir. */
  rotulo?: string;
  /**
   * Tem credencial gravada. Muda o QUE o botao faz, e por isso o que ele diz:
   * pareado, o clique RETOMA a sessao (barato); sem pareamento, ele registra um
   * aparelho novo na conta — a operacao pesada que o WhatsApp puniu em 11/08.
   */
  pareado?: boolean;
  /** Renderiza so o conteudo, sem o wrapper `Modal` (pra viver dentro da tela WhatsApp). */
  embutido?: boolean;
  /** Avisa a tela de cima que o estado mudou (religar, desconectar). */
  aoMudar?: () => void;
}

type EstadoLocal = BaileysStatus['state'] | 'qr' | 'loggedOut';

export function ModalConexaoBaileys({ aberto, onFechar, chip, rotulo, pareado, embutido, aoMudar }: Props) {
  const { socket } = useSocket();
  const toast = useToast();
  const [status, setStatus] = useState<BaileysStatus | null>(null);
  const [estado, setEstado] = useState<EstadoLocal>('close');
  const [qr, setQr] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [confirmaDesc, setConfirmaDesc] = useState(false);
  // A falha do clique NAO pode viver so no toast: ele some em 4s e o operador
  // fica com "pisquei e nao fez nada", que e' indistinguivel de botao morto.
  // Aqui a razao fica na tela ate a proxima tentativa.
  const [falha, setFalha] = useState<string | null>(null);

  const visivel = embutido || aberto;

  useEffect(() => {
    if (!visivel) return;
    let cancelado = false;
    setCarregando(true);
    // Zera o estado do aparelho anterior: o componente e' o mesmo pra todos, e
    // sem isto o QR de um apareceria por um instante dentro do painel do outro.
    setStatus(null);
    setEstado('close');
    setQr(null);
    setConfirmaDesc(false);
    setFalha(null);
    Promise.all([atendimentoApi.baileysStatus(chip), atendimentoApi.baileysQr(chip)])
      .then(([s, q]) => {
        if (cancelado) return;
        setStatus(s);
        setEstado(q.qr ? 'qr' : s.state);
        setQr(q.qr);
      })
      .catch((e: any) => {
        if (cancelado) return;
        const texto = [e?.name, e?.message].filter(Boolean).join(': ') || 'sem detalhe';
        setFalha(`Falha ao carregar o estado do celular — ${texto}`);
        toast.erro('Falha ao carregar o estado do celular');
      })
      .finally(() => !cancelado && setCarregando(false));
    return () => { cancelado = true; };
  }, [visivel, chip, toast]);

  useEffect(() => {
    if (!visivel || !socket) return;
    // O backend emite os eventos de TODOS os aparelhos no mesmo canal — o filtro
    // por `instance` e' o que impede o QR de um aparecer na tela do outro.
    function aoQr(data: { instance: string; qr: string }) {
      if (data.instance !== chip) return;
      setQr(data.qr);
      setEstado('qr');
    }
    function aoStatus(data: { instance: string; state: 'open' | 'close' | 'loggedOut' }) {
      if (data.instance !== chip) return;
      setEstado(data.state);
      // Some com o QR em QUALQUER saida do ar, nao so ao conectar. Desde que o
      // servidor parou de reabrir sozinho ([DA-511]), um QR que expirou nao vai
      // ser substituido por outro — deixar a imagem velha na tela e' pedir pro
      // atendente ficar mirando o celular num codigo que o WhatsApp ja recusa.
      setQr(null);
      aoMudar?.();
    }
    socket.on('baileys_qr', aoQr);
    socket.on('connection_status', aoStatus);
    return () => {
      socket.off('baileys_qr', aoQr);
      socket.off('connection_status', aoStatus);
    };
  }, [visivel, chip, socket, aoMudar]);

  async function reiniciar() {
    setCarregando(true);
    setFalha(null);
    try {
      await atendimentoApi.baileysReiniciar(chip);
      setEstado('connecting');
      toast.sucesso('Reconectando...');
      aoMudar?.();
    } catch (e: any) {
      // Guarda o NOME do erro junto: "TypeError: Failed to fetch" (chamada
      // barrada antes de sair do navegador) e um 503 do servidor sao dois
      // diagnosticos opostos, e a mensagem sozinha nao os separa.
      const texto = [e?.name, e?.message].filter(Boolean).join(': ') || 'Falha ao reconectar';
      setFalha(texto);
      toast.erro(texto);
    } finally {
      setCarregando(false);
    }
  }

  async function desconectar() {
    setCarregando(true);
    try {
      await atendimentoApi.baileysDesconectar(chip);
      setEstado('loggedOut');
      setQr(null);
      setConfirmaDesc(false);
      toast.sucesso('Pareamento removido. Clique em Reconectar e escaneie o QR com o celular.');
      aoMudar?.();
    } catch (e: any) {
      toast.erro(e?.message || 'Falha ao desconectar');
    } finally {
      setCarregando(false);
    }
  }

  const corpo = (
    <>
      {/* `status &&` e' o que impede a faixa de acusar "desligado" ENQUANTO o
          estado ainda esta chegando: `status` nasce null e o `!status?.ativo`
          sozinho dava um lampejo de laranja em todo celular ligado, toda vez que
          a gaveta abria — e o lampejo se le como resposta ao clique. */}
      {status && !status.ativo && (
        // Laranja SOLIDO + preto: "desligado" e atencao, nao alarme — e nao pode
        // depender de verde/vermelho (daltonismo).
        <div className="flex gap-2 items-start p-3 rounded-lg mb-4"
          style={{ backgroundColor: '#f97316', border: '1px solid #c2410c', color: '#000' }}>
          <AlertCircle size={18} style={{ color: '#000' }} className="flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-medium">Este celular esta desligado</div>
            <div className="text-xs mt-1">Ligue ele na chave acima antes de parear.</div>
          </div>
        </div>
      )}

      {falha && (
        // Fica ate a proxima tentativa, de proposito: o toast some em 4s e o
        // operador nao tem como reler o motivo.
        <div className="flex gap-2 items-start p-3 rounded-lg mb-4"
          style={{ backgroundColor: '#fee2e2', border: '1px solid #b91c1c', color: '#111' }}>
          <AlertCircle size={18} style={{ color: '#b91c1c' }} className="flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-medium">O clique nao passou</div>
            <div className="text-xs mt-1" style={{ wordBreak: 'break-word' }}>{falha}</div>
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        {/* O MESMO clique faz duas coisas de custo muito diferente, entao ele nao
            pode ter o mesmo nome nas duas. Pareado: retoma a sessao que o WhatsApp
            ja conhece. Sem pareamento: registra um aparelho NOVO na conta — e e'
            por isso que ele virou primario aqui e automatico em lugar nenhum. */}
        <button onClick={reiniciar} disabled={carregando || !status?.ativo}
          className={`${pareado ? 'btn-secundario' : 'btn-primario'} flex items-center gap-1.5 disabled:opacity-50`}
          style={{ display: 'inline-flex', whiteSpace: 'nowrap', flex: '0 0 auto' }}
          title={!status?.ativo ? 'Ligue o celular primeiro' : pareado ? 'Reabre a conexao (nao apaga o pareamento)' : 'Gera o QR pra escanear com o celular'}>
          {pareado
            ? <><RefreshCw size={14} className={carregando ? 'animate-spin' : ''} /> Reconectar</>
            : <><QrCode size={14} className={carregando ? 'animate-pulse' : ''} /> Gerar QR</>}
        </button>
        {!confirmaDesc ? (
          <button onClick={() => setConfirmaDesc(true)} disabled={carregando || estado !== 'open'}
            className="btn-perigo flex items-center gap-1.5 disabled:opacity-50"
            style={{ display: 'inline-flex', whiteSpace: 'nowrap', flex: '0 0 auto' }}
            title="Apaga o pareamento. So volta com o celular na mao, escaneando o QR.">
            <LogOut size={14} /> Desparear
          </button>
        ) : (
          <div className="flex items-center gap-2 flex-wrap">
            {/* O texto nomeia a consequencia: "desconectar" nao diz que exige o
                celular presencialmente pra voltar. */}
            <span className="text-xs" style={{ color: '#b91c1c' }}>
              So volta com o celular na mao!
            </span>
            <button onClick={() => setConfirmaDesc(false)} className="btn-secundario text-xs">Cancelar</button>
            <button onClick={desconectar} disabled={carregando} className="btn-perigo text-xs">Desparear</button>
          </div>
        )}
      </div>

      {qr && estado === 'qr' && (
        <div className="text-center">
          <p className="text-sm mb-3">
            No celular {rotulo ? <b>{rotulo}</b> : null}: <b>WhatsApp</b> &gt; <b>Aparelhos conectados</b> &gt; <b>Conectar um aparelho</b>
          </p>
          <img src={qr} alt="QR code para parear o celular" className="mx-auto rounded-lg border-2"
            style={{ borderColor: 'var(--panel-border, #e5e7eb)', width: 260, height: 260 }} />
          {/* Nao prometer mais "se renova sozinho": ele se renova enquanto ESTE
              socket viver, e quando as tentativas do WhatsApp acabam o servidor
              NAO abre outro ([DA-511]) — era essa reabertura infinita que gerou 73
              QRs em 11/08 e restringiu as duas contas da loja. */}
          <p className="text-xs text-gray-500 mt-2">
            Escaneie agora. Se o QR sumir antes de voce conseguir, clique de novo em <b>Gerar QR</b>.
          </p>
        </div>
      )}

      {estado === 'open' && (
        <div className="text-sm py-2 opacity-80">
          Conectado. Este celular esta recebendo mensagens.
          <div className="text-xs mt-2 opacity-70">
            Nao deslogue pelo "Aparelhos conectados" do celular fisico — as chaves importadas
            viram lixo e obrigam a reescanear tudo.
          </div>
        </div>
      )}

      {(estado === 'close' || estado === 'loggedOut') && !qr && (
        <div className="text-sm py-2 opacity-80">
          {estado === 'loggedOut'
            ? <>Pareamento removido. Clique em <b>Gerar QR</b> e escaneie com o celular na mao.</>
            : pareado
              ? <>Fora do ar. Clique em <b>Reconectar</b> — o pareamento continua valendo, nao precisa do celular.</>
              : <>Sem pareamento. Clique em <b>Gerar QR</b> e escaneie com o celular na mao.</>}
          {/* Por que nada acontece sozinho: o sistema tenta no maximo duas vezes e
              para. Sem esta linha, "fora do ar" parado parece travamento. */}
          <div className="text-xs mt-2 opacity-70">
            O sistema tenta reconectar sozinho duas vezes e para. Ele nunca gera QR por conta propria.
          </div>
        </div>
      )}

      {estado === 'connecting' && (
        <div className="text-sm py-2 flex items-center gap-2 opacity-80">
          <Loader2 size={16} className="animate-spin" /> Conectando...
        </div>
      )}
    </>
  );

  if (embutido) return corpo;

  return (
    <Modal aberto={!!aberto} onFechar={onFechar ?? (() => {})} titulo={`Conexao — ${rotulo ?? chip}`} largura={520}
      rodape={<button type="button" className="btn-secundario" onClick={onFechar}>Fechar</button>}>
      {corpo}
    </Modal>
  );
}
